/*
 * Maison Ferré buy box (W1, 2026-11-02). Loaded by sections/main-product.liquid after theme.js.
 * - Lot selector [data-mf-lot]: per-card prices, extra size selects, selected state.
 * - Live CTA total on every submit button of the product form ("Ajouter au panier — 239,90 €"),
 *   sticky bar included ([data-mf-sticky-price]).
 * - Gift block [data-mf-gifts]: lid value and total follow the pan count.
 * - Lot add: a submit with a lot of 2+ is caught before the theme's ProductForm and sent as ONE
 *   /cart/add.js request ({items}), then the same events as ProductForm: `variant:add` on the form and
 *   `cart:change` (baseEvent "variant:add") on <html>, which open and re-render <cart-drawer>.
 *   On error: `cart:error` on the form + `cart:refresh` on document, as the theme does.
 * Maths mirror the native automatic discounts: per cart line, round(line price x pct / 100) to the cent.
 */
(() => {
  if (window.MFBuybox) return;

  const FORMATS = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_with_space_separator: [2, ' ', '.'],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_no_decimals_with_space_separator: [0, ' ', '.'],
    amount_no_decimals_with_apostrophe_separator: [0, "'", '.'],
  };

  // Same output as the theme's formatMoney (theme.js), tags stripped for textContent.
  function money(cents, format) {
    const s = window.themeVariables?.settings || {};
    const f = format || (s.currencyCodeEnabled ? s.moneyWithCurrencyFormat : s.moneyFormat) || '{{amount}}';
    return f.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      const [precision, thousands, decimal] = FORMATS[key] || FORMATS.amount;
      const [int, frac] = (Math.round(cents) / 100).toFixed(precision).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    }).replace(/<[^>]*>/g, '').replace(/ €/g, '\u00a0€'); // shop format has a plain space: keep € with its amount
  }

  // "2:5,3:10" -> [[2,5],[3,10]] sorted by key.
  const pairs = (str) => String(str || '').split(',')
    .map((p) => p.split(':').map((x) => Number(String(x).trim())))
    .filter(([k, v]) => k > 0 && Number.isFinite(v))
    .sort((a, b) => a[0] - b[0]);

  const pctFor = (tiers, count) => tiers.reduce((pct, [min, v]) => (count >= min ? v : pct), 0);

  // Lid value in cents for n pans; beyond the last step the last gap repeats.
  function lidFor(ladder, n) {
    if (!ladder.length) return 0;
    const hit = ladder.find(([k]) => k === n);
    if (hit) return Math.round(hit[1] * 100);
    const [lastK, lastV] = ladder[ladder.length - 1];
    if (n < lastK) return Math.round(ladder[0][1] * 100);
    const step = ladder.length > 1 ? lastV - ladder[ladder.length - 2][1] : lastV;
    return Math.round((lastV + (n - lastK) * step) * 100);
  }

  // variants: [{id, price, compare}] in cents, one per pan. Same variant = one cart line (addLot merges them),
  // so the discount is pct of the LINE price, rounded to the cent once per line (PLAN §0), not once per unit.
  function lotTotals(variants, pct) {
    const lines = new Map();
    variants.forEach((v) => {
      const key = v.id ?? lines.size;
      const line = lines.get(key) || { price: v.price, compare: Math.max(v.compare || 0, v.price), qty: 0 };
      line.qty += 1;
      lines.set(key, line);
    });
    let total = 0;
    let compare = 0;
    let gross = 0;
    lines.forEach((l) => {
      gross += l.price * l.qty;
      total += l.price * l.qty - Math.round((l.price * l.qty * pct) / 100);
      compare += l.compare * l.qty;
    });
    // save = what the lot discount takes off (owner 2026-11-04), never the compare-at gap; compare is still painted struck.
    return { total, compare, save: gross - total, unit: variants.length ? Math.round(total / variants.length) : 0 };
  }

  // Translated strings rendered by snippets/product-info.liquid (<script data-mf-i18n>, locales mf.buybox.*).
  let i18n = null;
  const t = (key, fallback) => {
    if (!i18n) {
      try { i18n = JSON.parse(document.querySelector('script[data-mf-i18n]')?.textContent || '{}'); } catch (e) { i18n = {}; }
    }
    return typeof i18n[key] === 'string' && i18n[key] ? i18n[key] : fallback;
  };
  const ctaLabel = (label, price) => t('cta', '@@label@@ — @@price@@').replace('@@label@@', () => label).replace('@@price@@', () => price);

  const rate = () => Number(window.Shopify?.currency?.rate) || 1;
  const formName = (form) => form?.getAttribute('id') || '';
  const variantIdOf = (form) => Number(form?.elements?.namedItem('id')?.value) || 0;

  /* ---------- lot selector ---------- */

  const lots = new Map(); // form id -> lot api

  function initLot(root) {
    const form = document.forms[root.dataset.form];
    let variants = [];
    try { variants = JSON.parse(root.querySelector('[data-mf-variants]')?.textContent || '[]'); } catch (e) { variants = []; }
    if (!form || !variants.length) return;

    const byId = new Map(variants.map((v) => [v.id, v]));
    const tiers = pairs(root.dataset.tiers);
    const cards = Array.from(root.querySelectorAll('[data-mf-lot-card]'));
    const selected = () => cards.find((c) => c.querySelector('input[type="radio"]')?.checked) || cards[0];
    const idsOf = (card) => [variantIdOf(form), ...Array.from(card.querySelectorAll('select[data-mf-extra]'), (s) => Number(s.value))].filter(Boolean);

    const api = {
      qty: () => Number(selected()?.dataset.qty) || 1,
      ids: () => idsOf(selected()),
      total: () => {
        const card = selected();
        return lotTotals(idsOf(card).map((id) => byId.get(id)).filter(Boolean), pctFor(tiers, Number(card.dataset.qty) || 1)).total;
      },
      render() {
        const mainId = variantIdOf(form);
        cards.forEach((card) => {
          const n = Number(card.dataset.qty) || 1;
          card.querySelectorAll('select[data-mf-extra]').forEach((s) => {
            if (!s.dataset.touched && byId.has(mainId)) s.value = String(mainId);
          });
          const t = lotTotals(idsOf(card).map((id) => byId.get(id)).filter(Boolean), pctFor(tiers, n));
          const set = (sel, text) => { const el = card.querySelector(sel); if (el) el.textContent = text; };
          set('[data-mf-unit]', money(t.unit));
          set('[data-mf-total]', money(t.total));
          set('[data-mf-compare]', money(t.compare));
          set('[data-mf-save]', money(t.save));
          card.querySelector('[data-mf-compare]')?.toggleAttribute('hidden', t.compare <= t.total);
          card.querySelector('[data-mf-save-line]')?.toggleAttribute('hidden', t.save <= 0);
          const on = card === selected();
          card.classList.toggle('is-selected', on);
          card.querySelector('[data-mf-extras]')?.toggleAttribute('hidden', !on);
        });
      },
    };

    root.addEventListener('change', (event) => {
      if (event.target.matches('select[data-mf-extra]')) event.target.dataset.touched = '1';
      api.render();
      refresh(form);
    });

    lots.set(formName(form), api);
    api.render();
  }

  /* ---------- CTA, sticky bar, gifts ---------- */

  const lastPrice = new Map(); // form id -> unit price (cents) of the current variant

  function refresh(form, variant) {
    const id = formName(form);
    if (!id) return;
    if (variant) lastPrice.set(id, variant.price);
    const lot = lots.get(id);
    if (lot) lot.render();
    const total = lot ? lot.total() : lastPrice.get(id);
    const label = window.themeVariables?.strings?.addToCartButton || '';

    if (Number.isFinite(total) && label) {
      const buttons = new Set([
        ...Array.from(form.querySelectorAll('button[type="submit"]')),
        ...Array.from(document.querySelectorAll(`button[type="submit"][form="${CSS.escape(id)}"]`)),
      ]);
      buttons.forEach((button) => {
        const sticky = button.querySelector('[data-mf-sticky-price]');
        if (sticky) { sticky.textContent = money(total); return; }
        const target = button.getAttribute('is') === 'custom-button' && button.firstElementChild ? button.firstElementChild : button;
        // Only relabel an add-to-cart label (never "Épuisé" / pre-order / unavailable).
        if (button.disabled || !target.textContent.trim().toLowerCase().startsWith(label.toLowerCase())) return;
        target.setAttribute('aria-live', 'polite'); // announce the new total (REVIEW M2)
        target.textContent = ctaLabel(label, money(total));
      });
    }

    document.querySelectorAll(`[data-mf-gifts][data-form="${CSS.escape(id)}"]`).forEach((gifts) => {
      const pans = lot ? lot.qty() : Number(gifts.dataset.pans) || 1;
      const r = rate();
      const guide = Math.round((Number(gifts.dataset.guide) || 0) * r);
      const protect = Math.round((Number(gifts.dataset.protect) || 0) * r);
      const lid = Math.round(lidFor(pairs(gifts.dataset.ladder || '1:29.95,2:59.95,3:89.95,4:119.95'), pans) * r);
      const set = (sel, cents) => { const el = gifts.querySelector(sel); if (el) el.textContent = money(cents); };
      set('[data-mf-gift-value="guide"]', guide);
      set('[data-mf-gift-value="protect"]', protect);
      set('[data-mf-gift-value="lid"]', lid);
      set('[data-mf-gift-total]', guide + protect + lid);
    });
  }

  /* ---------- lot add to cart ---------- */

  async function addLot(form, lot) {
    const counts = new Map();
    lot.ids().forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
    const items = Array.from(counts, ([id, quantity]) => ({ id, quantity }));
    if (!items.length) return;

    const buttons = [
      ...Array.from(form.querySelectorAll('button[type="submit"]')),
      ...Array.from(document.querySelectorAll(`button[type="submit"][form="${CSS.escape(formName(form))}"]`)),
    ];
    buttons.forEach((b) => { b.setAttribute('disabled', 'disabled'); b.setAttribute('aria-busy', 'true'); });

    const root = window.Shopify?.routes?.root || '/';
    const sections = ['variant-added'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: sections.join(','), sections_url: `${root}variants/${variantIdOf(form)}` }),
      });
      const json = await response.json();
      if (!response.ok) throw json;

      const s = window.themeVariables?.settings || {};
      if (s.cartType === 'page' || s.pageType === 'cart') {
        window.location.href = `${root}cart`;
        return;
      }
      const cart = await (await fetch(`${root}cart.js`)).json();
      cart.sections = json.sections;
      form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: json.items || [json], cart } }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
    } catch (error) {
      form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: error?.description || error?.message || '' } }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      buttons.forEach((b) => { b.removeAttribute('disabled'); b.removeAttribute('aria-busy'); });
    }
  }

  // Capture phase on document: runs before ProductForm's own submit listener on the form.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    const lot = form instanceof HTMLFormElement ? lots.get(formName(form)) : null;
    if (!lot || lot.qty() < 2) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!form.checkValidity()) { form.reportValidity(); return; }
    addLot(form, lot);
  }, true);

  // After the theme's own variant:change listeners (BuyButtons resets the label).
  document.addEventListener('variant:change', (event) => {
    const form = event.target instanceof HTMLFormElement ? event.target : null;
    if (form) setTimeout(() => refresh(form, event.detail?.variant), 0);
  });

  function init() {
    document.querySelectorAll('[data-mf-lot]').forEach(initLot);
    const forms = new Set(Array.from(document.querySelectorAll('[data-mf-lot], [data-mf-gifts]'), (el) => document.forms[el.dataset.form]).filter(Boolean));
    forms.forEach((form) => refresh(form));
  }

  window.MFBuybox = { money, pairs, pctFor, lidFor, lotTotals, refresh, ctaLabel };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Trust line "FREE shipping to <country>": Liquid renders the Shopify Markets country, which falls back to the
  // market's default (US on the international store) until the visitor picks a country. Until that choice exists
  // (no `localization` cookie), use the region of the browser language (nl-NL -> Nederland) with Intl.DisplayNames.
  (function shipCountry() {
    const el = document.querySelector('[data-mf-ship]');
    if (!el || /(^|;\s*)localization=/.test(document.cookie)) return;
    const region = ((navigator.language || '').split('-')[1] || '').toUpperCase();
    if (!/^[A-Z]{2}$/.test(region) || region === el.dataset.iso || typeof Intl === 'undefined' || !Intl.DisplayNames) return;
    try {
      const lang = document.documentElement.lang || 'fr';
      const name = new Intl.DisplayNames([lang], { type: 'region' }).of(region);
      if (!name) return;
      el.querySelector('[data-mf-ship-text]').textContent = (el.dataset.template || '').replace('@@country@@', name);
      const flag = el.querySelector('[data-mf-ship-flag]');
      if (flag) { flag.className = flag.className.replace(/country-flags--\w+/, 'country-flags--' + region); }
    } catch (e) { /* keep the Liquid value */ }
  })();

  // PFAS report popup: the product section is its own stacking context, so the sticky header painted over the modal
  // (owner 2026-11-04 06:25). Portal the modal root to <body> once; listeners survive the move (same nodes).
  (function portalReport() {
    const move = () => document.querySelectorAll('[data-mf-report-root]').forEach((root) => { if (root.parentElement !== document.body) document.body.appendChild(root); });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', move); else move();
  })();

  // Lab card over the gallery: on phones the carousel scrolls under it, so hide it as soon as slide 1 is left
  // (owner 2026-11-04 06:50: it covered text baked into later images). Desktop grid: the card sits on image 1 only.
  (function labChipOnFirstSlide() {
    const chip = document.querySelector('.mf-lab-chip');
    const list = document.querySelector('.product-gallery__media-list');
    if (!chip || !list) return;
    let raf = 0;
    const update = () => { raf = 0; chip.classList.toggle('is-off', list.scrollLeft > list.clientWidth * 0.25); };
    list.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
    update();
  })();
})();
